const COLORS = ['#0f766e', '#2563eb', '#c2410c', '#7c3aed', '#be123c'];

export const BLS_QCEW_DATA_GUIDE_URL = 'https://www.bls.gov/cew/about-data/data-files-guide.htm';

function sortTooltipItemsByVisualOrder(left, right) {
  const leftY = Number(left?.element?.y);
  const rightY = Number(right?.element?.y);
  if (!Number.isFinite(leftY) || !Number.isFinite(rightY)) return 0;
  return leftY - rightY;
}

function shortTitle(value) {
  return String(value || '')
    .replace(/^NAICS\s+[\d-]+\s+/i, '')
    .replace(/\s+industries$/i, '');
}

export function findMissingIndustryRuns(labels, datasets) {
  return datasets.flatMap((dataset, datasetIndex) => {
    const runs = [];
    let startIndex = null;

    dataset.data.forEach((value, index) => {
      const isInternalGap = value == null
        && dataset.data.slice(0, index).some((item) => item != null)
        && dataset.data.slice(index + 1).some((item) => item != null);
      if (isInternalGap && startIndex == null) startIndex = index;
      if (!isInternalGap && startIndex != null) {
        runs.push({ datasetIndex, startIndex, endIndex: index - 1 });
        startIndex = null;
      }
    });

    if (startIndex != null) {
      runs.push({ datasetIndex, startIndex, endIndex: dataset.data.length - 1 });
    }

    return runs;
  });
}

export function buildIndustryTrendChart(payload) {
  const series = Array.isArray(payload?.series) ? payload.series : [];
  const periods = Array.from(new Set(
    series.flatMap((item) => item.observations?.map((row) => row.period) || []),
  )).sort((left, right) => {
    const [leftYear, leftQuarter] = left.split(' Q').map(Number);
    const [rightYear, rightQuarter] = right.split(' Q').map(Number);
    return (leftYear * 4 + leftQuarter) - (rightYear * 4 + rightQuarter);
  });
  const data = {
    labels: periods,
    datasets: series.map((item, index) => {
      const values = new Map(item.observations.map((row) => [row.period, row.employment]));
      return {
        label: shortTitle(item.industry_title),
        data: periods.map((period) => values.get(period) ?? null),
        borderColor: COLORS[index % COLORS.length],
        backgroundColor: `${COLORS[index % COLORS.length]}14`,
        borderWidth: 2.2,
        pointRadius: 0,
        pointHoverRadius: 3,
        pointBackgroundColor: COLORS[index % COLORS.length],
        pointBorderWidth: 0,
        spanGaps: false,
        tension: 0.24,
      };
    }),
  };
  const withheldRuns = findMissingIndustryRuns(data.labels, data.datasets);
  withheldRuns.forEach((run) => {
    const sourceDataset = data.datasets[run.datasetIndex];
    const beforeValue = Number(sourceDataset.data[run.startIndex - 1]);
    const afterValue = Number(sourceDataset.data[run.endIndex + 1]);
    const placeholderValues = periods.map(() => null);
    const stepCount = run.endIndex - run.startIndex + 2;
    for (let index = run.startIndex; index <= run.endIndex; index += 1) {
      const offset = index - run.startIndex + 1;
      placeholderValues[index] = Number.isFinite(beforeValue) && Number.isFinite(afterValue)
        ? beforeValue + ((afterValue - beforeValue) * (offset / stepCount))
        : (Number.isFinite(beforeValue) ? beforeValue : afterValue);
    }
    data.datasets.push({
      label: sourceDataset.label,
      data: placeholderValues,
      borderColor: sourceDataset.borderColor,
      backgroundColor: sourceDataset.borderColor,
      pointBackgroundColor: sourceDataset.borderColor,
      pointRadius: 0,
      pointHoverRadius: 0,
      pointBorderWidth: 0,
      showLine: false,
      isWithheldPlaceholder: true,
    });
  });
  return {
    data,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#64748b',
            usePointStyle: false,
            boxWidth: 18,
            boxHeight: 2,
            padding: 12,
            font: { size: 10 },
            filter: (item, chartData) => !chartData.datasets[item.datasetIndex]?.isWithheldPlaceholder,
          },
        },
        tooltip: {
          itemSort: sortTooltipItemsByVisualOrder,
          callbacks: {
            label: (context) => context.dataset.isWithheldPlaceholder
              ? `${context.dataset.label}: BLS withheld employment data`
              : `${context.dataset.label}: ${Number(context.parsed.y).toLocaleString()} jobs`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6,
            font: { size: 10 },
          },
        },
        y: {
          beginAtZero: false,
          grid: { color: 'rgba(148, 163, 184, 0.2)' },
          ticks: {
            color: '#64748b',
            callback: (value) => Number(value).toLocaleString(),
            font: { size: 10 },
          },
        },
      },
    },
  };
}
